import { EditorSelection, Prec } from "@codemirror/state";
import { keymap } from "@codemirror/view";

import { ancestorNodeOfType } from "components/organisms/manuscript/editor/latex/tree/ancestors";
import { getListType } from "components/organisms/manuscript/editor/latex/tree/lists";
import { createListItem } from "components/organisms/manuscript/editor/visual/utils/list-item";
import {
    cursorIsAtStartOfListItem,
    indentDecrease,
    indentIncrease,
} from "components/organisms/manuscript/editor/commands/commands";

export const visualKeymap = Prec.highest(
    keymap.of([
        {
            key: "Enter",
            run: (view) => {
                const { state } = view;
                let handled = false;

                const changes = state.changeByRange((range) => {
                    if (!range.empty) {
                        return { range };
                    }

                    const { from } = range;
                    const listNode = ancestorNodeOfType(state, from, "ListEnvironment");
                    if (listNode) {
                        const line = state.doc.lineAt(range.from);
                        const endLine = state.doc.lineAt(listNode.to);

                        if (line.number === endLine.number - 1 && /^\\item(\[])?$/.test(line.text.trim())) {
                            const pos = listNode.to + 1;
                            const deleteCurrentLine = {
                                from: line.from,
                                to: line.to + 1,
                                insert: "",
                            };

                            const localChanges = [deleteCurrentLine];
                            let nextRange;

                            if (listNode.parent?.parent?.parent?.parent?.type.is("ListEnvironment")) {
                                const newListItem = createListItem(state, pos);
                                localChanges.push({
                                    from: pos,
                                    insert: `${newListItem}\n`,
                                });
                                nextRange = EditorSelection.cursor(pos + newListItem.length);
                            } else {
                                nextRange = EditorSelection.cursor(pos);
                            }

                            handled = true;

                            return {
                                changes: localChanges,
                                range: nextRange.map(state.changes(deleteCurrentLine)),
                            };
                        }

                        let insert = `\n${createListItem(state, from)}`;
                        const countWhitespaceAfterPosition = (pos) => {
                            const line = state.doc.lineAt(pos);
                            const followingText = state.sliceDoc(pos, line.to);
                            const matches = followingText.match(/^(\s+)/);
                            return matches ? matches[1].length : 0;
                        };
                        let pos;

                        if (getListType(state, listNode) === "description") {
                            insert = insert.replace(/\\item $/, "\\item[] ");
                            pos = from + insert.length - 2;
                        } else {
                            pos = from + insert.length + countWhitespaceAfterPosition(from);
                        }

                        handled = true;
                        return {
                            changes: { from, insert },
                            range: EditorSelection.cursor(pos, -1),
                        };
                    }

                    const sectioningNode = ancestorNodeOfType(state, from, "SectioningCommand");
                    if (sectioningNode) {
                        const nextLineNumber = state.doc.lineAt(from).number + 1;
                        if (nextLineNumber <= state.doc.lines) {
                            const line = state.doc.line(nextLineNumber);
                            handled = true;
                            return {
                                range: EditorSelection.cursor(line.from),
                            };
                        }
                    }

                    return { range };
                });

                if (handled) {
                    view.dispatch(changes, {
                        scrollIntoView: true,
                        userEvent: "input",
                    });
                }

                return handled;
            },
        },
        {
            key: "Mod-]",
            preventDefault: true,
            run: indentIncrease,
        },
        {
            key: "Mod-[",
            preventDefault: true,
            run: indentDecrease,
        },
        {
            key: "Tab",
            preventDefault: true,
            run: (view) => cursorIsAtStartOfListItem(view.state) && indentIncrease(view),
        },
        {
            key: "Shift-Tab",
            preventDefault: true,
            run: indentDecrease,
        },
    ]),
);
